import React, { useState, useEffect } from 'react';
import { getDashboardMembre } from '../services/api';
import { FiDollarSign, FiHash, FiMapPin, FiFileText, FiUser, FiMail, FiPhone, FiBriefcase, FiLayers } from 'react-icons/fi';

const formatMoney = (n: number) => new Intl.NumberFormat('fr-FR').format(n) + ' FCFA';

const MembreDashboard = () => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getDashboardMembre().then(r => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) return <div>Chargement...</div>;
  if (!data) return <div>Erreur de chargement</div>;

  const { user, versements, parcelles, nbDocuments, totalVersements, nbVersements, messages } = data;

  return (
    <div>
      <h1 className="page-title">Mon espace membre</h1>

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-icon" style={{ background: '#e8f5e9', color: '#2e7d32' }}><FiDollarSign /></div>
          <div className="kpi-info">
            <span className="kpi-value">{formatMoney(totalVersements || 0)}</span>
            <span className="kpi-label">Total versements</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon" style={{ background: '#e3f2fd', color: '#1565c0' }}><FiHash /></div>
          <div className="kpi-info">
            <span className="kpi-value">{nbVersements || 0}</span>
            <span className="kpi-label">Nombre de versements</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon" style={{ background: '#fff3e0', color: '#e65100' }}><FiMapPin /></div>
          <div className="kpi-info">
            <span className="kpi-value">{parcelles?.length || 0}</span>
            <span className="kpi-label">Parcelles attribuées</span>
          </div>
        </div>
        <div className="kpi-card">
          <div className="kpi-icon" style={{ background: '#f3e5f5', color: '#7b1fa2' }}><FiFileText /></div>
          <div className="kpi-info">
            <span className="kpi-value">{nbDocuments || 0}</span>
            <span className="kpi-label">Documents</span>
          </div>
        </div>
      </div>

      {/* Two columns: Infos + Parcelles */}
      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <span><FiUser /> Mes informations</span>
          </div>
          <div className="card-body">
            <div className="info-list">
              <div className="info-row"><span className="info-label">Nom complet</span><span className="info-value">{user?.prenomnom || '-'}</span></div>
              <div className="info-row"><span className="info-label">Matricule</span><span className="info-value">{user?.matricule || '-'}</span></div>
              <div className="info-row"><span className="info-label">Email</span><span className="info-value">{user?.email || '-'}</span></div>
              <div className="info-row"><span className="info-label">Téléphone</span><span className="info-value">{user?.telephone || '-'}</span></div>
              <div className="info-row"><span className="info-label">Service</span><span className="info-value">{user?.service || '-'}</span></div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span><FiMapPin /> Mes parcelles</span>
          </div>
          <div className="card-body">
            {(!parcelles || parcelles.length === 0) ? (
              <p style={{ color: '#888', textAlign: 'center', padding: '2rem 0' }}>Aucune parcelle attribuée</p>
            ) : (
              <div className="info-list">
                {parcelles.map((p: any, i: number) => (
                  <div key={p.id || i} style={{ padding: '0.75rem 0', borderBottom: '1px solid #eee' }}>
                    <strong>Parcelle {p.numero}</strong> - {p.projet_libelle || 'N/A'}
                    <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                      Superficie: {p.superficie || '-'} m² | Lot: {p.lot || '-'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Derniers versements */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <span><FiDollarSign /> Derniers versements</span>
        </div>
        <div className="card-body">
          {(!versements || versements.length === 0) ? (
            <p style={{ color: '#888', textAlign: 'center', padding: '2rem 0' }}>Aucun versement enregistré</p>
          ) : (
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr><th>Date</th><th>Type</th><th>Référence</th><th>Montant</th></tr>
                </thead>
                <tbody>
                  {versements.slice(0, 10).map((v: any) => (
                    <tr key={v.id}>
                      <td>{v.date ? new Date(v.date).toLocaleDateString('fr-FR') : '-'}</td>
                      <td><span className="badge badge-blue">{v.type || '-'}</span></td>
                      <td>{v.reference || '-'}</td>
                      <td style={{ fontWeight: 600 }}>{formatMoney(v.montant || 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Messages */}
      {messages && messages.length > 0 && (
        <div className="card" style={{ marginTop: '1.5rem' }}>
          <div className="card-header">
            <span><FiMail /> Messages</span>
          </div>
          <div className="card-body">
            {messages.map((m: any) => (
              <div key={m.id} style={{ padding: '0.75rem 0', borderBottom: '1px solid #eee' }}>
                <div style={{ fontWeight: 600 }}>{m.objet || m.sujet || 'Message'}</div>
                <div style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>{m.contenu || m.message || ''}</div>
                <div style={{ fontSize: '0.75rem', color: '#999', marginTop: '0.25rem' }}>
                  {m.created_at ? new Date(m.created_at).toLocaleDateString('fr-FR') : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default MembreDashboard;
